import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Key,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Trash2,
  Lock,
  Building2,
  Globe,
  Mail,
  Server,
  CreditCard,
  Shield,
  RefreshCw,
  Search,
  Users,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// Générer une clé de sécurité (3 lettres + 3 chiffres)
const generateSecurityKey = () => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  let key = '';
  for (let i = 0; i < 3; i++) key += letters[Math.floor(Math.random() * letters.length)];
  for (let i = 0; i < 3; i++) key += digits[Math.floor(Math.random() * digits.length)];
  return key;
};

const categoryIcons = {
  website: Globe,
  email: Mail,
  hosting: Server,
  payment: CreditCard,
  social: Users,
  other: Key
};

const categoryLabels = {
  website: 'Site Web',
  email: 'Email',
  hosting: 'Hébergement',
  payment: 'Paiement',
  social: 'Réseaux sociaux',
  other: 'Autre'
};

const SharedPasswordsPage = () => {
  const { user, isAgency, isClient } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDirection, setFilterDirection] = useState('all'); // 'all', 'to_client', 'from_client'
  const [filterClient, setFilterClient] = useState('all');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPassword, setSelectedPassword] = useState(null);
  const [securityKeyInput, setSecurityKeyInput] = useState('');
  const [decryptedPassword, setDecryptedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [newPassword, setNewPassword] = useState({
    client_id: '',
    title: '',
    username: '',
    password: '',
    url: '',
    category: 'website',
    notes: '',
    direction: 'to_client' // 'to_client' or 'from_client'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [passwordsRes, clientsRes] = await Promise.all([
        api.get('/shared-passwords/'),
        isAgency ? api.get('/users/?role=client') : Promise.resolve({ data: [] })
      ]);
      setPasswords(passwordsRes.data);
      if (isAgency) setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newPassword.title || !newPassword.password) {
      toast.error('Titre et mot de passe requis');
      return;
    }
    
    if (isAgency && !newPassword.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    try {
      const securityKey = generateSecurityKey();
      const payload = {
        ...newPassword,
        security_key: securityKey
      };
      
      await api.post('/shared-passwords/', payload);
      toast.success(`Accès créé ! Clé de sécurité: ${securityKey}`, {
        duration: 10000,
        description: 'Notez cette clé, elle ne sera plus affichée.'
      });
      setShowCreateModal(false);
      setNewPassword({
        client_id: '',
        title: '',
        username: '',
        password: '',
        url: '',
        category: 'website',
        notes: '',
        direction: 'to_client'
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleViewPassword = async (password) => {
    setSelectedPassword(password);
    setSecurityKeyInput('');
    setDecryptedPassword('');
    setShowViewModal(true);
  };

  const handleVerifySecurityKey = async () => {
    if (!securityKeyInput) {
      toast.error('Veuillez entrer la clé de sécurité');
      return;
    }

    try {
      const res = await api.post(`/shared-passwords/${selectedPassword.id}/verify`, {
        security_key: securityKeyInput.toUpperCase()
      });
      setDecryptedPassword(res.data.password);
      toast.success('Accès vérifié');
    } catch (error) {
      toast.error('Clé de sécurité incorrecte');
    }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cet accès partagé ?')) return;
    
    try {
      await api.delete(`/shared-passwords/${id}`);
      toast.success('Accès supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredPasswords = passwords.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.username?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDirection = filterDirection === 'all' || p.direction === filterDirection;
    const matchesClient = filterClient === 'all' || p.client_id === filterClient;
    return matchesSearch && matchesDirection && matchesClient;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-[#1a365d] flex items-center gap-2">
              <Key className="w-5 h-5" />
              Mots de passe partagés
            </h1>
            <p className="text-sm text-[#64748b]">
              Échangez des accès de manière sécurisée avec vos {isAgency ? 'clients' : 'l\'agence'}
            </p>
          </div>
          
          <Button 
            onClick={() => setShowCreateModal(true)}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Partager un accès
          </Button>
        </div>

        {/* Info Card */}
        <Card className="card mb-6 bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-pink-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#1a365d] mb-1">Sécurité renforcée</h3>
                <p className="text-sm text-[#64748b]">
                  Chaque accès partagé est protégé par une clé de sécurité unique (ex: ABC123) 
                  qui n'est jamais transmise automatiquement. Communiquez cette clé par un canal 
                  séparé (téléphone, SMS) pour plus de sécurité.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher un accès..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-default"
            />
          </div>
          
          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les accès</SelectItem>
              <SelectItem value="to_client">
                <span className="flex items-center gap-2">
                  <ArrowRight className="w-3 h-3" /> Vers {isAgency ? 'client' : 'moi'}
                </span>
              </SelectItem>
              <SelectItem value="from_client">
                <span className="flex items-center gap-2">
                  <ArrowLeft className="w-3 h-3" /> De {isAgency ? 'client' : 'l\'agence'}
                </span>
              </SelectItem>
            </SelectContent>
          </Select>

          {isAgency && clients.length > 0 && (
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.company_name || `${client.first_name} ${client.last_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Passwords List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredPasswords.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <Lock className="w-16 h-16 mx-auto mb-4 text-[#94a3b8]" />
              <h3 className="text-lg font-semibold text-[#1a365d] mb-2">Aucun accès partagé</h3>
              <p className="text-[#64748b] mb-4">
                Commencez par partager un accès avec {isAgency ? 'un client' : 'l\'agence'}
              </p>
              <Button onClick={() => setShowCreateModal(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Partager un accès
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPasswords.map(password => {
              const CategoryIcon = categoryIcons[password.category] || Key;
              return (
                <Card 
                  key={password.id} 
                  className="card card-hover cursor-pointer"
                  onClick={() => handleViewPassword(password)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-xl flex items-center justify-center",
                          password.direction === 'to_client' ? "bg-emerald-100" : "bg-blue-100"
                        )}>
                          <CategoryIcon className={cn(
                            "w-5 h-5",
                            password.direction === 'to_client' ? "text-emerald-600" : "text-blue-600"
                          )} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#1a365d]">{password.title}</h4>
                          <p className="text-xs text-[#64748b]">{categoryLabels[password.category]}</p>
                        </div>
                      </div>
                      <Badge className={cn(
                        "text-xs",
                        password.direction === 'to_client' 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-blue-100 text-blue-700"
                      )}>
                        {password.direction === 'to_client' ? (
                          <><ArrowRight className="w-3 h-3 mr-1" /> Envoyé</>
                        ) : (
                          <><ArrowLeft className="w-3 h-3 mr-1" /> Reçu</>
                        )}
                      </Badge>
                    </div>
                    
                    {password.username && (
                      <p className="text-sm text-[#64748b] truncate mb-1">
                        Utilisateur: {password.username}
                      </p>
                    )}
                    {password.url && (
                      <p className="text-sm text-[#64748b] truncate mb-2">
                        URL: {password.url}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between pt-2 border-t border-[#e2e8f0]">
                      <span className="text-xs text-[#94a3b8]">
                        {new Date(password.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="text-xs text-pink-500 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Protégé
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-pink-500" />
                Partager un accès
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {isAgency && (
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Select 
                    value={newPassword.client_id} 
                    onValueChange={(v) => setNewPassword({...newPassword, client_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          <span className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {client.company_name || `${client.first_name} ${client.last_name}`}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Direction</Label>
                <Select 
                  value={newPassword.direction} 
                  onValueChange={(v) => setNewPassword({...newPassword, direction: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to_client">
                      <span className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-emerald-500" />
                        Je partage vers {isAgency ? 'le client' : 'l\'agence'}
                      </span>
                    </SelectItem>
                    <SelectItem value="from_client">
                      <span className="flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4 text-blue-500" />
                        {isAgency ? 'Le client' : 'L\'agence'} me partage
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Titre *</Label>
                <Input
                  value={newPassword.title}
                  onChange={(e) => setNewPassword({...newPassword, title: e.target.value})}
                  placeholder="Ex: Accès WordPress, Compte Google Ads..."
                  className="input-default"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select 
                    value={newPassword.category} 
                    onValueChange={(v) => setNewPassword({...newPassword, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categoryLabels).map(([key, label]) => {
                        const Icon = categoryIcons[key];
                        return (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              {label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>URL</Label>
                  <Input
                    value={newPassword.url}
                    onChange={(e) => setNewPassword({...newPassword, url: e.target.value})}
                    placeholder="https://..."
                    className="input-default"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Identifiant / Email</Label>
                <Input
                  value={newPassword.username}
                  onChange={(e) => setNewPassword({...newPassword, username: e.target.value})}
                  placeholder="admin@exemple.com"
                  className="input-default"
                />
              </div>

              <div className="space-y-2">
                <Label>Mot de passe *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword.password}
                    onChange={(e) => setNewPassword({...newPassword, password: e.target.value})}
                    placeholder="••••••••"
                    className="input-default pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Input
                  value={newPassword.notes}
                  onChange={(e) => setNewPassword({...newPassword, notes: e.target.value})}
                  placeholder="Informations complémentaires..."
                  className="input-default"
                />
              </div>

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700">
                    Une clé de sécurité unique sera générée. Transmettez-la par téléphone ou SMS 
                    pour que le destinataire puisse accéder au mot de passe.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreate} className="btn-primary">
                <Lock className="w-4 h-4 mr-2" />
                Créer l'accès sécurisé
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Password Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-pink-500" />
                {selectedPassword?.title}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!decryptedPassword ? (
                <>
                  <div className="text-center py-4">
                    <Lock className="w-12 h-12 mx-auto mb-3 text-pink-500" />
                    <p className="text-[#64748b] mb-4">
                      Entrez la clé de sécurité pour accéder au mot de passe
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Clé de sécurité</Label>
                    <Input
                      value={securityKeyInput}
                      onChange={(e) => setSecurityKeyInput(e.target.value.toUpperCase())}
                      placeholder="ABC123"
                      className="input-default text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                    />
                  </div>
                  
                  <Button onClick={handleVerifySecurityKey} className="w-full btn-primary">
                    <Key className="w-4 h-4 mr-2" />
                    Vérifier
                  </Button>
                </>
              ) : (
                <>
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-emerald-700 font-medium">Accès vérifié</span>
                  </div>

                  {selectedPassword?.username && (
                    <div className="space-y-1">
                      <Label className="text-xs text-[#64748b]">Identifiant</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={selectedPassword.username}
                          readOnly
                          className="input-default"
                        />
                        <Button 
                          size="icon" 
                          variant="outline"
                          onClick={() => handleCopy(selectedPassword.username, 'Identifiant')}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <Label className="text-xs text-[#64748b]">Mot de passe</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={decryptedPassword}
                        readOnly
                        className="input-default font-mono"
                      />
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={() => handleCopy(decryptedPassword, 'Mot de passe')}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {selectedPassword?.url && (
                    <div className="space-y-1">
                      <Label className="text-xs text-[#64748b]">URL</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={selectedPassword.url}
                          readOnly
                          className="input-default"
                        />
                        <Button 
                          size="icon" 
                          variant="outline"
                          onClick={() => window.open(selectedPassword.url, '_blank')}
                        >
                          <Globe className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {selectedPassword?.notes && (
                    <div className="space-y-1">
                      <Label className="text-xs text-[#64748b]">Notes</Label>
                      <p className="text-sm text-[#334155] p-2 bg-slate-50 rounded">{selectedPassword.notes}</p>
                    </div>
                  )}

                  {isAgency && (
                    <Button 
                      variant="destructive" 
                      className="w-full mt-4"
                      onClick={() => {
                        handleDelete(selectedPassword.id);
                        setShowViewModal(false);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer cet accès
                    </Button>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SharedPasswordsPage;
